interface PrintColumn<T> {
  label: string;
  align?: "left" | "right" | "center";
  render: (row: T) => string | number;
}

export interface PrintReportOptions<T> {
  title: string;
  subtitle?: string;
  rangeLabel?: string;
  columns: PrintColumn<T>[];
  rows: T[];
  summary?: { label: string; value: string }[];
  footerNote?: string;
}

function esc(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Opens a new window with a clean, print-ready version of the report.
 * User then picks "Save as PDF" from the browser's print dialog.
 */
export function printReport<T>(opts: PrintReportOptions<T>) {
  const now = new Date().toLocaleString("en-GB");
  const rowsHtml = opts.rows.map(row =>
    `<tr>${opts.columns.map(col =>
      `<td style="text-align:${col.align || "left"}">${esc(col.render(row))}</td>`
    ).join("")}</tr>`
  ).join("");

  const summaryHtml = opts.summary && opts.summary.length > 0
    ? `<div class="summary">${opts.summary.map(s => `<div><span class="lbl">${esc(s.label)}</span><span class="val">${esc(s.value)}</span></div>`).join("")}</div>`
    : "";

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>${esc(opts.title)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1f2937; padding: 24px; margin: 0; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  .sub { color: #6b7280; font-size: 13px; margin-bottom: 18px; }
  .summary { display: flex; gap: 24px; background: #f9fafb; padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; font-size: 13px; }
  .summary .lbl { display: block; color: #6b7280; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
  .summary .val { display: block; font-weight: 600; font-size: 15px; margin-top: 2px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th { text-align: left; padding: 8px 10px; border-bottom: 2px solid #e5e7eb; background: #f9fafb; }
  td { padding: 7px 10px; border-bottom: 1px solid #f3f4f6; }
  tr:nth-child(even) td { background: #fafafa; }
  .foot { margin-top: 20px; font-size: 11px; color: #9ca3af; }
  .actions { margin-bottom: 16px; }
  .actions button { padding: 8px 14px; font-size: 13px; border: 1px solid #d1d5db; border-radius: 6px; background: #fff; cursor: pointer; margin-right: 8px; }
  .actions button.primary { background: #111827; color: #fff; border-color: #111827; }
  @media print {
    .actions { display: none; }
    body { padding: 0; }
  }
</style>
</head>
<body>
  <div class="actions">
    <button class="primary" onclick="window.print()">Print / Save as PDF</button>
    <button onclick="window.close()">Close</button>
  </div>
  <h1>${esc(opts.title)}</h1>
  <div class="sub">
    ${opts.subtitle ? esc(opts.subtitle) + " · " : ""}${opts.rangeLabel ? "Period: " + esc(opts.rangeLabel) + " · " : ""}Generated ${esc(now)}
  </div>
  ${summaryHtml}
  <table>
    <thead>
      <tr>${opts.columns.map(c => `<th style="text-align:${c.align || "left"}">${esc(c.label)}</th>`).join("")}</tr>
    </thead>
    <tbody>${rowsHtml || `<tr><td colspan="${opts.columns.length}" style="text-align:center; padding:40px; color:#9ca3af">No data</td></tr>`}</tbody>
  </table>
  ${opts.footerNote ? `<div class="foot">${esc(opts.footerNote)}</div>` : ""}
  <script>
    window.addEventListener('load', function() { setTimeout(function(){ window.print(); }, 300); });
  </script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=1000,height=800");
  if (!w) {
    alert("Could not open print window — please allow pop-ups for this site.");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
